<template>
  <div
    ref="input"
    class="tkCustomPulldown"
    :style="styleVars"
  >
    <tk-text-box
      :theme="theme"
      :size="size"
      :width="width"
      :value="label"
      :disabled="disabled"
      readonly
      :color="textColor"
      :no-border="noBorder"
      :bold="bold"
      :active="active"
      :cursor="cursor"
      :icon="textIcon"
      :put-icon-outside="putTextIconOutside"
      :icon-color="textIconColor"
      :icon-separator="textIconSeparator"
      :_extra-text-padding-left="iconPosition === 'left' && icon !== 'none' ? 16 : 0"
      :_extra-text-padding-right="iconPosition === 'right' && icon !== 'none' ? 16 : 0"
      @click="openOptions"
    />
    <div
      v-if="isOptionsExpanded"
      :class="[
        'tkCustomPulldown_options',
        '-animated',
        { [`-${textColor}`]: !!textColor },
      ]"
      :style="styleVars"
      @mouseenter="justOpenedOptions = false"
    >
      <div
        v-for="item in items"
        :key="item.value"
        :class="['tkCustomPulldown_option', { '-focused': justOpenedOptions && selectedValue === item.value }]"
        :style="styleVars"
        @click="selectItem(item.value)"
      >
        {{ item.label }}
      </div>
    </div>
    <div
      v-if="isOptionsExpanded"
      class="tkCustomPulldown_outside"
      @click="isOptionsExpanded = false"
    />
    <tk-icon
      v-if="!!iconName"
      :class="[
        'tkCustomPulldown_icon',
        `-${iconPosition}`,
        icon ? `-${icon}` : '',
        { '-putTextIconOutside': putTextIconOutside },
        { '-disabled': disabled },
      ]"
      :style="styleVars"
      :name="iconName"
      :color="computedIconColor"
      @click="openOptions"
    />
  </div>
</template>

<script>
import TkIcon from '../TkIcon/TkIcon.vue'
import TkTextBox from '../TkTextBox/TkTextBox.vue'
import { input } from '../../../mixins'

export default {
  name: 'TkCustomPulldown',

  components: { TkIcon, TkTextBox },

  mixins: [input],

  model: {
    // 要素が選択されると 選択された要素の value をペイロードとする change イベントを発行する
    event: 'change',
  },

  props: {
    // ---------------
    // 機能に関するもの

    value: {
      type: String,
      default: '',
    },
    // {
    //   label: オプションとして表示する文字列
    //   value: change イベントで選択要素として連携される ID
    // }
    // の配列
    items: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // items に存在しない value を設定したときに表示される文字列
    unselectedLabel: {
      type: String,
      default: undefined,
    },

    // ---------------
    // 外観に関するもの

    // スタイルプリセット
    // default: 白を基調とした基本的なスタイル
    // flat: フラットデザイン
    theme: {
      type: String,
      default: 'default',
    },
    // 文字サイズと高さのプリセット
    // 'normal', 'large', 'x-large', 'xx-large', 'xxx-large' が指定可能
    // 高さはそれぞれ 30, 36, 40, 45, 50
    size: {
      type: String,
      default: 'normal',
    },
    width: {
      type: Number,
      default: undefined,
    },
    noBorder: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    // true で色を反転させる
    active: {
      type: Boolean,
      default: false,
    },
    icon: {
      // アイコン名
      // none | arrow | spin | filled-spin
      // none の場合は非表示
      type: String,
      default: 'arrow',
    },
    iconColor: {
      // アイコンの色指定
      type: String,
      default: undefined,
    },
    iconPosition: {
      // アイコンを左右どちらに配置するか
      type: String,
      default: 'right',
    },
    cursor: {
      type: String,
      default: 'pointer',
    },
    textColor: {
      type: String,
      default: undefined,
    },
    textIcon: {
      // テキスト横に表示するアイコン名
      type: String,
      default: undefined,
    },
    putTextIconOutside: {
      // アイコンの位置をボックスの外側にする
      type: Boolean,
      default: false,
    },
    textIconColor: {
      // テキスト横に表示するアイコンの色指定
      type: String,
      default: undefined,
    },
    textIconSeparator: {
      // テキスト横に表示するアイコンの区切り線
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    isOptionsExpanded: false,
    justOpenedOptions: false,
    optionsTop: 0,  // オプション上端
    initialOptionsTranslationY: '0%',  // 展開の中心点
  }),

  computed: {
    label() {
      const selectedItem = this.items.find(i => i.value === this.selectedValue)
      return selectedItem
        ? selectedItem.label
        : this.unselectedLabel ? this.unselectedLabel : ''
    },
    selectedValue() {
      const correspondingOption = this.items
        .find(i => i.value || i.value === 0
          ? String(i.value) === this.value
          : !this.value)
      return correspondingOption ? correspondingOption.value : 'unselected-value'
    },
    iconClasses() {
      return [
        'tkCustomPulldown_icon',
        this.icon ? `-${this.icon}` : '',
        this.iconPosition === 'right' ? '-right' : '-left',
      ]
    },
    iconName() {
      if (!this.icon) return null

      switch (this.icon) {
        case 'arrow':
          return 'arrow_left'
        case 'spin':
          return 'spin'
        case 'filled-spin':
          return 'filled_spin'
        default: return null
      }
    },
    // 現状、'active' が指定された時は例外なく白にしており、text-color="blue" のときくらいしか正常な見た目にならない
    // カラーごとに個別に対応するべき
    computedIconColor() {
      return this.active ? 'white' : this.iconColor
    },
    inputHeight() {
      return Number(this.styleVars['--height'].slice(0, -2))  // 'px' の前を切り取る
    },
    optionHeight() {
      switch (this.size) {
        case 'normal': return 28
        case 'large': return 34
        case 'x-large': return 36
        case 'xx-large': return 38
        case 'xxx-large': return 40
        default: return 28
      }
    },
    optionsPadding() {
      switch (this.size) {
        case 'normal': return 4
        case 'large': return 5
        case 'x-large': return 5
        case 'xx-large': return 5
        case 'xxx-large': return 5
        default: return 4
      }
    },
    optionPaddingLeft() {
      switch (this.size) {
        case 'normal': return 6
        case 'large': return 8
        case 'x-large': return 9
        case 'xx-large': return 9
        case 'xxx-large': return 10
        default: return 6
      }
    },
    styleVars() {
      return {
        ...this.__sizingVars(this.size, this.textIcon, this.textIconSeparator, this.putTextIconOutside),
        '--width': this.width ? `${this.width}px` : '100%',
        '--option-height': `${this.optionHeight}px`,
        '--options-padding': `${this.optionsPadding}px`,
        '--option-padding-left': `${this.optionPaddingLeft}px`,
        '--options-top': `${this.optionsTop}px`,
        '--options-initial-translation-y': this.initialOptionsTranslationY,
        '--cursor': this.cursor,
      }
    },
  },

  methods: {
    openOptions() {
      this.isOptionsExpanded = true
      this.justOpenedOptions = true

      const inputTop = this.$refs.input.getBoundingClientRect().top
      const inputBottom = inputTop + this.inputHeight
      const actualOptionHeight = this.optionHeight
      const optionsHeight = Math.min(
        actualOptionHeight * this.items.length + this.optionsPadding * 2,
        400,  // max-height 400px を固定値で指定しているため、計算をそれに合わせる
        window.innerHeight,  // 最大でもウィンドウの高さまで
      )

      // 下に出せるか判断する
      const expectedOptionsBottom = inputBottom + optionsHeight
      const shouldOpenOptionsAboveInput = expectedOptionsBottom > window.innerHeight

      // オプション上端
      this.optionsTop = shouldOpenOptionsAboveInput
        ? Math.max(  // 画面上端より上にはみ出ないようにする
          -optionsHeight - 5,  // 5 は微調整
          -inputTop,  // input 基準 absolute では画面上端を指す
        )
        : this.inputHeight

      // 最上端に開く場合を 0、普通に下に開く場合を 1 としたときの top 位置
      const topRatio = (this.optionsTop + optionsHeight + 5) / (this.inputHeight + optionsHeight + 5)
      // 展開の中心点 (常に input 下端をスタート位置として展開するようにする)
      this.initialOptionsTranslationY = `${50 - topRatio * 100}%`
    },
    selectItem(value) {
      this.$emit('change', value)
      this.isOptionsExpanded = false
    },
  },
}
</script>

<style scoped lang="scss">
.tkCustomPulldown {
  position: relative;
  width: var(--width);
}

.tkCustomPulldown_options {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: var(--options-top);
  left: var(--outside-icon-space);
  z-index: 20;
  min-width: calc(var(--width) - var(--outside-icon-space));
  max-height: 400px;
  padding: var(--options-padding);
  overflow: auto;
  border-radius: 6px;
  box-shadow: 2px 4px 4px rgba(0, 0, 0, 0.1);
  background-color: white;
  cursor: var(--cursor);

  // color
  &.-blue {
    color: #1F6AAD;
  }

  &.-animated {
    animation: popup 0.2s cubic-bezier(0.22, 1, 0.36, 1) 1 forwards;
  }
  @keyframes popup {
    0% {
      transform: translateY(var(--options-initial-translation-y)) scaleY(0);
      opacity: 0;
    }
    100% {
      transform: translateY(0) scaleY(1.0);
    }
    80%, 100% {
      opacity: 1;
    }
  }
}

.tkCustomPulldown_option {
  height: var(--option-height);
  padding: 0 15px 0 var(--option-padding-left);
  border-radius: 2px;
  font-size: var(--font-size);
  line-height: var(--option-height);
  white-space: nowrap;
  user-select: none;

  &.-focused {
    background-color: #eaf2f8;
  }

  &:hover {
    background-color: #eaf2f8;
  }
}

.tkCustomPulldown_outside {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 19;  // オプションの 20 よりは低くする
  width: 100%;
  height: 100%;
  background-color: transparent;
}

.tkCustomPulldown_icon {
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  cursor: var(--cursor);

  // size for each icon
  &.-arrow {
    --icon-width: 8px;
    transform: rotate(-90deg);
  }
  &.-spin {
    --icon-width: 10px;
  }
  &.-filled-spin {
    --icon-width: 10px;
  }
  width: var(--icon-width);

  &.-left {
    left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + 10px - var(--icon-width));

    &.-putTextIconOutside {
      left: calc(var(--text-padding-x) + 10px - var(--icon-width) + var(--outside-icon-space));
    }
  }
  &.-right {
    right: calc(var(--icon-width) + 7px);
  }

  &.-disabled {
    opacity: .5;
  }
}
</style>
